import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import type { AppRole } from '@infras/shared-types';
import { CopyButton } from '../components/CopyButton';
import { AgentCommandDialog } from '../components/AgentCommandDialog';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { ScannedAt } from '../components/ScannedAt';
import { SortHeader } from '../components/SortHeader';
import { useMe } from '../lib/useMe';
import { compareBy, useUrlClear, useUrlSort, useUrlState } from '../lib/useUrlState';
import { formatDateTime } from '../lib/format';
import { timeAgo, type AgentRelease, type AgentVersionRollup } from '../lib/agentCommands';

/**
 * Agent fleet — DaemonSet agents that report node egress IPs for Atlas IP
 * whitelisting (migration from controller.opstech.org), and, since the command
 * plane, the surface an operator issues work from: per-cluster desired version +
 * rollout progress, and per-node logs / restart through the approve-first dialog.
 */

const AGENTS = gql`
  query Agents {
    agents {
      agentId
      clusterName
      nodeName
      publicIp
      status
      failedHealthChecks
      lastSeenAt
      agentVersion
      agentImage
      isLeader
      leaderHolderIdentity
      leaderObservedAt
      capabilities
    }
    agentStatusEvents(limit: 20) {
      id
      agentId
      event
      detail
      createdAt
    }
    clusterAgentTokens {
      clusterName
      isActive
      description
      lastUsedAt
    }
    agentFleetVersions {
      clusterName
      desiredVersion
      total
      onDesired
      leaderAgentId
      commandsEnabled
      isProduction
    }
    agentReleases {
      version
      image
      notes
      isDefault
      publishedBy
      publishedAt
    }
  }
`;

const MINT = gql`
  mutation MintClusterAgentToken($clusterName: String!, $description: String) {
    mintClusterAgentToken(clusterName: $clusterName, description: $description) {
      clusterName
      token
    }
  }
`;

const ROTATE = gql`
  mutation RotateClusterAgentToken($clusterName: String!) {
    rotateClusterAgentToken(clusterName: $clusterName) {
      clusterName
      token
    }
  }
`;

const SET_ACTIVE = gql`
  mutation SetClusterAgentTokenActive($clusterName: String!, $active: Boolean!) {
    setClusterAgentTokenActive(clusterName: $clusterName, active: $active)
  }
`;

// The server-side half of the command gate. The agent has its own independent
// AGENT_COMMANDS_ENABLED flag in its ConfigMap; BOTH must be on. Root only —
// `isProduction` decides whether a maintainer may approve a mutating command at all,
// so it is not a cosmetic label.
const SET_CLUSTER_SETTINGS = gql`
  mutation SetClusterAgentSettings(
    $clusterName: String!
    $commandsEnabled: Boolean
    $isProduction: Boolean
  ) {
    setClusterAgentSettings(
      clusterName: $clusterName
      commandsEnabled: $commandsEnabled
      isProduction: $isProduction
    ) {
      clusterName
      commandsEnabled
      isProduction
    }
  }
`;

interface Agent {
  agentId: string;
  clusterName: string;
  nodeName?: string | null;
  publicIp?: string | null;
  status: string;
  failedHealthChecks: number;
  lastSeenAt: string;
  agentVersion?: string | null;
  agentImage?: string | null;
  isLeader?: boolean | null;
  leaderHolderIdentity?: string | null;
  leaderObservedAt?: string | null;
  /** Ops this build advertises. Null/empty = the agent hasn't reported any yet. */
  capabilities?: string[] | null;
}
interface StatusEvent {
  id: string;
  agentId: string;
  event: string;
  detail?: string | null;
  createdAt: string;
}
interface ClusterToken {
  clusterName: string;
  isActive: boolean;
  description?: string | null;
  lastUsedAt?: string | null;
}

// status → status-dot level (reuses .health-dot ok/warn/down/none) + display label
const STATUS_META: Record<string, { dot: string; label: string }> = {
  active: { dot: 'ok', label: 'Active' },
  pending_removal: { dot: 'warn', label: 'Pending Removal' },
  removal_failed: { dot: 'down', label: 'Removal failed' },
  removed: { dot: 'none', label: 'Removed' },
  inactive: { dot: 'none', label: 'Inactive' },
};
const statusMeta = (s: string) => STATUS_META[s] ?? { dot: 'none', label: s };

interface AgentGroup {
  clusterName: string;
  rows: Agent[];
  active: number;
  pending: number;
  other: number;
}

/** What the command dialog was opened for. */
interface DialogTarget {
  clusterName: string;
  op: string;
  agentId?: string | null;
  initialArgs?: Record<string, string>;
}

/** A pending root-only flip of one server-side gate, awaiting confirmation. */
interface SettingsChange {
  clusterName: string;
  field: 'commandsEnabled' | 'isProduction';
  next: boolean;
}

/**
 * An agent only runs ops its build advertises. Until it has reported any we
 * can't tell, so we allow the click — the server (and the agent's own
 * deny-by-default registry) is the enforcement point either way.
 */
const supportsOp = (a: Agent, op: string) => !a.capabilities?.length || a.capabilities.includes(op);

/** Default row order inside a cluster: problems first, then most recently seen.
 *  Kept as the `status` sort so the useful default IS a named, re-selectable
 *  column rather than something you lose the moment you click any header. */
const STATUS_RANK = (s: string) => (s === 'active' ? 0 : s === 'pending_removal' ? 1 : 2);

const SORT_PICK: Record<string, (a: Agent) => unknown> = {
  node: (a) => a.nodeName || a.agentId,
  publicIp: (a) => a.publicIp,
  agentVersion: (a) => a.agentVersion,
  isLeader: (a) => !!a.isLeader,
  lastSeenAt: (a) => a.lastSeenAt,
};

function sortAgents(rows: Agent[], sort: ReturnType<typeof useUrlSort>): Agent[] {
  if (sort.key === 'status') {
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort(
      (a, b) =>
        (STATUS_RANK(a.status) - STATUS_RANK(b.status)) * sign ||
        new Date(b.lastSeenAt).getTime() - new Date(a.lastSeenAt).getTime(),
    );
  }
  return compareBy(rows, (a) => SORT_PICK[sort.key]?.(a), sort.dir);
}

export function AgentsPage() {
  const { data, loading, error, refetch } = useQuery<{
    agents: Agent[];
    agentStatusEvents: StatusEvent[];
    clusterAgentTokens: ClusterToken[];
    agentFleetVersions: AgentVersionRollup[];
    agentReleases: AgentRelease[];
  }>(AGENTS, { fetchPolicy: 'cache-and-network' });
  const { me } = useMe();
  const role: AppRole = me?.role ?? 'new_member';
  // Preview + draft are root|maintainer on the server — don't offer the buttons
  // to anyone who would only get a 403.
  const canIssue = role === 'root' || role === 'maintainer';
  // setClusterAgentSettings is @RequireRole('root') server-side.
  const canSetSettings = role === 'root';

  const [dialog, setDialog] = useState<DialogTarget | null>(null);
  const [settingsConfirm, setSettingsConfirm] = useState<SettingsChange | null>(null);
  const [setClusterSettings, setClusterSettingsState] = useMutation(SET_CLUSTER_SETTINGS, {
    onCompleted: () => {
      setSettingsConfirm(null);
      refetch();
    },
  });

  // Cluster / status / text filters and the row sort live in the URL, so
  // "/agents?cluster=k8s-optisigns-prod-api" is a link — the command detail
  // page's rail sends you here that way.
  const [clusterFilter, setClusterFilter] = useUrlState('cluster');
  const [statusFilter, setStatusFilter] = useUrlState('status');
  const [q, setQ] = useUrlState('q');
  const sort = useUrlSort('status');

  const agents = data?.agents ?? [];
  const events = data?.agentStatusEvents ?? [];
  const tokens = data?.clusterAgentTokens ?? [];
  const releases = data?.agentReleases ?? [];
  const rollups = useMemo(
    () => new Map((data?.agentFleetVersions ?? []).map((r) => [r.clusterName, r])),
    [data?.agentFleetVersions],
  );
  const clusterNames = useMemo(
    () => [...new Set(agents.map((a) => a.clusterName))].sort(),
    [agents],
  );
  // The fleet's freshness is the newest heartbeat in it — one silent agent is a
  // per-row problem, but the whole fleet going quiet means the register endpoint
  // or the token is broken.
  const newestSeen = useMemo(
    () => agents.map((a) => a.lastSeenAt).filter(Boolean).sort().at(-1),
    [agents],
  );

  const needle = q.trim().toLowerCase();
  const matching = useMemo(
    () =>
      agents.filter(
        (a) =>
          (!clusterFilter || a.clusterName === clusterFilter) &&
          (!statusFilter || a.status === statusFilter) &&
          (!needle ||
            (a.nodeName ?? '').toLowerCase().includes(needle) ||
            a.agentId.toLowerCase().includes(needle) ||
            (a.publicIp ?? '').toLowerCase().includes(needle)),
      ),
    [agents, clusterFilter, statusFilter, needle],
  );

  // Group the fleet by cluster (legacy layout): each group carries active/pending/other counts.
  const groups = useMemo<AgentGroup[]>(() => {
    const byCluster = new Map<string, Agent[]>();
    for (const a of matching) {
      const arr = byCluster.get(a.clusterName) ?? [];
      arr.push(a);
      byCluster.set(a.clusterName, arr);
    }
    return [...byCluster.entries()]
      .map(([clusterName, list]) => {
        const active = list.filter((a) => a.status === 'active').length;
        const pending = list.filter((a) => a.status === 'pending_removal').length;
        return {
          clusterName,
          rows: sortAgents(list, sort),
          active,
          pending,
          other: list.length - active - pending,
        };
      })
      .sort((a, b) => a.clusterName.localeCompare(b.clusterName));
  }, [matching, sort]);

  // Groups start collapsed; a cluster is open only once expanded (so new clusters default collapsed too).
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleGroup = (name: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  // Filtering to one cluster is an act of asking to SEE it — arriving at a
  // collapsed group would make the deep link look like it had matched nothing.
  const isOpen = (name: string) => expanded.has(name) || groups.length === 1;
  const anyFilter = Boolean(clusterFilter || statusFilter || needle);
  const clearFilters = useUrlClear(['cluster', 'status', 'q']);

  return (
    <div className="page page--wide operations-page agents-page">
      <div className="detail-head">
        <h1>Agents</h1>
        <span className="badge quiet">
          {agents.length} agent{agents.length === 1 ? '' : 's'}
        </span>
        <button type="button" className="btn btn-ghost" disabled={loading} onClick={() => void refetch().catch(() => undefined)}>
          Refresh
        </button>
        <span className="spacer" />
        <Link className="btn btn-ghost" to="/agents/commands">
          Command log
        </Link>
      </div>
      <p className="detail-desc">Monitor agent health, versions, and node connectivity across clusters. <Link to="/docs/agents">Agent guide</Link></p>

      {error && (
        <>
          <p className="error">Couldn&apos;t load the fleet — the API didn&apos;t respond.</p>
          <p className="muted">
            Agents keep heartbeating regardless; this page only reads. If it persists, ask in
            #devops-infra.
          </p>
        </>
      )}

      <div className="detail-sections">
        <section>
          <h3>Fleet</h3>
          {/* An hour, not a day: agents heartbeat every few minutes, so a fleet
              whose newest beat is an hour old is already a finding. */}
          <ScannedAt at={newestSeen} staleAfterH={1} lead="Newest heartbeat in the fleet" />

          <div className="filter-row">
            <input
              type="search"
              aria-label="Search agents" placeholder="Search node, agent ID, or IP"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <select
              value={clusterFilter}
              onChange={(e) => setClusterFilter(e.target.value)}
              aria-label="Filter by cluster"
            >
              <option value="">All clusters</option>
              {clusterNames.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="">Any status</option>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
            {anyFilter && <button className="mini-btn" onClick={clearFilters}>Clear filters</button>}
            <span className="filter-count">
              {matching.length} of {agents.length}
            </span>
          </div>

          {loading && !agents.length ? (
            <div className="skeleton-row-group">
              {[0, 1, 2, 3].map((i) => (
                <div className="skeleton-row" key={i} />
              ))}
            </div>
          ) : !agents.length ? (
            <EmptyState
              icon="cluster"
              title="No agents yet"
              body="An agent appears here the first time its DaemonSet registers with a per-cluster token. Mint one below, then roll the DaemonSet onto the cluster."
              actionLabel="How agents work"
              actionTo="/docs/agents"
            />
          ) : !groups.length ? (
            <p className="muted">
              No agent matches these filters.{' '}
              {anyFilter && (
                <button className="mini-btn" onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </p>
          ) : (
            <div className="agent-groups">
              {groups.map((g) => {
                const open = isOpen(g.clusterName);
                const roll = rollups.get(g.clusterName);
                const desired = roll?.desiredVersion ?? null;
                const onDesired = roll ? roll.onDesired === roll.total : false;
                const commandsEnabled = roll?.commandsEnabled ?? false;
                const bodyId = `agents-${g.clusterName.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                // Point the upgrade at the release the cluster is supposed to be on
                // (else the default release) so the operator isn't typing image refs.
                const rel =
                  releases.find((r) => r.version === desired) ?? releases.find((r) => r.isDefault) ?? null;
                return (
                  <div className="agent-group" key={g.clusterName}>
                    {/* Header is a ROW, not a button — action controls live inside it
                        and a <button> inside a <button> is invalid (Firefox/Safari
                        swallow the inner click). Only the label toggles. */}
                    <div className="agent-group-head">
                      <button
                        type="button"
                        className="agent-group-toggle"
                        aria-expanded={open}
                        aria-controls={bodyId}
                        onClick={() => toggleGroup(g.clusterName)}
                      >
                        <span className={`agent-caret ${open ? 'open' : ''}`}>▸</span>
                        <strong>{g.clusterName}</strong>
                        <span className="badge quiet">
                          {g.rows.length} agent{g.rows.length === 1 ? '' : 's'}
                        </span>
                        {desired && <span className="badge">desired {desired}</span>}
                        {roll && (
                          <span className={`agent-count ${onDesired ? 'ok' : 'warn'}`}>
                            {roll.onDesired}/{roll.total} on desired
                          </span>
                        )}
                        <span className="spacer" />
                        {g.active > 0 && <span className="agent-count ok">{g.active} active</span>}
                        {g.pending > 0 && <span className="agent-count warn">{g.pending} pending</span>}
                        {g.other > 0 && <span className="agent-count muted">{g.other} other</span>}
                      </button>
                      <div className="agent-group-actions">
                        {/* Root can flip both server-side gates here; everyone else sees
                            the same information as a read-only badge. Enabling commands is
                            only HALF the gate — the cluster's agents also need
                            AGENT_COMMANDS_ENABLED in their ConfigMap. */}
                        {roll?.isProduction &&
                          (canSetSettings ? (
                            <button
                              type="button"
                              className="badge prod as-button"
                              title="Clear the production flag — lets a maintainer approve mutating commands here"
                              onClick={() =>
                                setSettingsConfirm({
                                  clusterName: g.clusterName,
                                  field: 'isProduction',
                                  next: false,
                                })
                              }
                            >
                              production
                            </button>
                          ) : (
                            <span className="badge prod">production</span>
                          ))}
                        {/* Same pill for everyone — a maintainer deciding whether to
                            approve needs to see the gate too; only root can click it. */}
                        {canSetSettings ? (
                          <button
                            type="button"
                            className={`badge gate as-button ${commandsEnabled ? 'gate-on' : 'gate-off'}`}
                            title={
                              commandsEnabled
                                ? 'Commands are enabled server-side — click to disable'
                                : 'Commands are disabled server-side — click to enable'
                            }
                            onClick={() =>
                              setSettingsConfirm({
                                clusterName: g.clusterName,
                                field: 'commandsEnabled',
                                next: !commandsEnabled,
                              })
                            }
                          >
                            <span className="gate-dot" aria-hidden="true" />
                            commands {commandsEnabled ? 'on' : 'off'}
                          </button>
                        ) : (
                          <span
                            className={`badge gate ${commandsEnabled ? 'gate-on' : 'gate-off'}`}
                            title={
                              commandsEnabled
                                ? 'Commands are enabled for this cluster'
                                : 'Commands are disabled for this cluster'
                            }
                          >
                            <span className="gate-dot" aria-hidden="true" />
                            commands {commandsEnabled ? 'on' : 'off'}
                          </span>
                        )}
                        {/* The read-only rung of the rollout ladder needs a way in. With
                            commands on but AGENT_MUTATING_OPS_ENABLED / pod.logs still off,
                            an agent advertises only pod.list + workload.status — and every
                            other control on this row issues an op it is not advertising, so
                            the safe intermediate state had nothing you could actually run. */}
                        {canIssue && (
                          <button
                            type="button"
                            className="mini-btn"
                            disabled={!commandsEnabled}
                            title={
                              commandsEnabled
                                ? 'List pods in the agent namespace — read-only, the safest way to prove the command path'
                                : 'Commands are disabled for this cluster'
                            }
                            onClick={() =>
                              setDialog({
                                clusterName: g.clusterName,
                                op: 'pod.list',
                                initialArgs: { namespace: 'platform' },
                              })
                            }
                          >
                            Pods
                          </button>
                        )}
                        {canIssue && (
                          <button
                            type="button"
                            className="mini-btn"
                            disabled={!commandsEnabled || onDesired}
                            title={
                              !commandsEnabled
                                ? 'Commands are disabled for this cluster'
                                : onDesired
                                  ? 'Every agent is already on the desired version'
                                  : `Roll the DaemonSet to ${rel?.version ?? 'a published release'}`
                            }
                            onClick={() =>
                              setDialog({
                                clusterName: g.clusterName,
                                op: 'agent.self_upgrade',
                                initialArgs: rel ? { image: rel.image, version: rel.version } : {},
                              })
                            }
                          >
                            Upgrade
                          </button>
                        )}
                        <Link
                          className="mini-btn"
                          to={`/agents/commands?cluster=${encodeURIComponent(g.clusterName)}`}
                        >
                          Commands
                        </Link>
                      </div>
                    </div>
                    {open && (
                      <div className="operations-table-scroll" tabIndex={0} role="region" aria-label="Agent records"><table className="mtx agent-table" id={bodyId}>
                        <thead>
                          {/* One `sort` shared by every open group — a column means
                              the same thing in each, and per-group sort state would
                              make two clusters disagree about what "Version ↑" is. */}
                          <tr>
                            <SortHeader
                              label="Status"
                              sortKey="status"
                              sort={sort}
                              title="Sort by status — problems first, then most recently seen"
                            />
                            <SortHeader label="Node name" sortKey="node" sort={sort} />
                            <SortHeader label="IP address" sortKey="publicIp" sort={sort} />
                            <SortHeader label="Version" sortKey="agentVersion" sort={sort} />
                            <SortHeader label="Leader" sortKey="isLeader" sort={sort} />
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.rows.map((a) => {
                            const meta = statusMeta(a.status);
                            const outdated = !!desired && !!a.agentVersion && a.agentVersion !== desired;
                            return (
                              <tr key={a.agentId}>
                                <td>
                                  <span className="dot-cell" title={meta.label}>
                                    <span className={`health-dot ${meta.dot}`} />
                                    <span className={meta.dot === 'none' ? 'muted' : ''}>{meta.label}</span>
                                  </span>
                                </td>
                                <td>
                                  {/* Node names, IPs and version strings are resource
                                      identifiers, not code — Plex Sans per the mono
                                      policy ratified in phase 6 §1. */}
                                  <div className="agent-node">{a.nodeName || a.agentId}</div>
                                  <div className="agent-sub muted">Last seen: {timeAgo(a.lastSeenAt)}</div>
                                  {a.status === 'pending_removal' && (
                                    <div className="agent-sub agent-sub-warn">
                                      Auto: {a.failedHealthChecks} health check
                                      {a.failedHealthChecks === 1 ? '' : 's'} failed
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <span className="ip-cell">{a.publicIp || '—'}</span>
                                  {a.publicIp && <CopyButton value={a.publicIp} />}
                                </td>
                                <td>
                                  {a.agentVersion || '—'}
                                  {outdated && <span className="badge inferred">outdated</span>}
                                  {a.agentImage && (
                                    <div className="agent-sub muted" title={a.agentImage}>
                                      {a.agentImage.split('/').pop()}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  {a.isLeader ? (
                                    <>
                                      <span className="badge confirmed">leader</span>
                                      <div className="agent-sub muted">
                                        seen {timeAgo(a.leaderObservedAt)}
                                      </div>
                                    </>
                                  ) : (
                                    <span className="muted">—</span>
                                  )}
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  {canIssue ? (
                                    <>
                                      <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        disabled={!commandsEnabled || !supportsOp(a, 'pod.logs')}
                                        title={opTitle(commandsEnabled, supportsOp(a, 'pod.logs'), 'pod.logs')}
                                        onClick={() =>
                                          setDialog({
                                            clusterName: g.clusterName,
                                            op: 'pod.logs',
                                            agentId: a.agentId,
                                            initialArgs: { namespace: 'platform' },
                                          })
                                        }
                                      >
                                        Logs
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        disabled={!commandsEnabled || !supportsOp(a, 'daemonset.restart')}
                                        title={opTitle(
                                          commandsEnabled,
                                          supportsOp(a, 'daemonset.restart'),
                                          'daemonset.restart',
                                        )}
                                        onClick={() =>
                                          setDialog({
                                            clusterName: g.clusterName,
                                            op: 'daemonset.restart',
                                            agentId: a.agentId,
                                            initialArgs: { name: 'optisigns-agent', namespace: 'platform' },
                                          })
                                        }
                                      >
                                        Restart
                                      </button>
                                    </>
                                  ) : (
                                    <span className="muted text-2xs">root / maintainer only</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table></div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <ClusterTokensSection tokens={tokens} onChange={() => refetch()} />

        <section style={{ marginTop: '1.5rem' }}>
          <h3>Recent events</h3>
          {!events.length ? (
            <p className="muted">No agent events yet.</p>
          ) : (
            <div className="operations-table-scroll" tabIndex={0} role="region" aria-label="Agent records"><table className="mtx">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Agent</th>
                  <th>Event</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id}>
                    <td className="muted">{formatDateTime(e.createdAt)}</td>
                    <td>{e.agentId}</td>
                    <td>{e.event}</td>
                    <td className="muted">{e.detail || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </section>
      </div>

      {dialog && (
        <AgentCommandDialog
          clusterName={dialog.clusterName}
          op={dialog.op}
          agentId={dialog.agentId}
          initialArgs={dialog.initialArgs}
          role={role}
          onClose={() => {
            setDialog(null);
            refetch();
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(settingsConfirm)}
        title={settingsConfirm ? settingsConfirmTitle(settingsConfirm) : ''}
        body={settingsConfirm ? settingsConfirmBody(settingsConfirm) : undefined}
        confirmLabel={settingsConfirmLabel(settingsConfirm)}
        // Loosening a gate is the risky direction, so that is what renders red:
        // turning commands ON, or clearing the production flag.
        danger={
          settingsConfirm
            ? settingsConfirm.field === 'isProduction'
              ? !settingsConfirm.next
              : settingsConfirm.next
            : false
        }
        busy={setClusterSettingsState.loading}
        onCancel={() => setSettingsConfirm(null)}
        onConfirm={() => {
          if (!settingsConfirm) return;
          void setClusterSettings({
            variables: {
              clusterName: settingsConfirm.clusterName,
              [settingsConfirm.field]: settingsConfirm.next,
            },
          });
        }}
      />
    </div>
  );
}

/** Name the ACTION, not the boolean — "Disable" for clearing a production flag reads
 *  as "disable the cluster", which is the opposite of what the click does. */
function settingsConfirmLabel(c: SettingsChange | null): string {
  if (!c) return 'Confirm';
  if (c.field === 'isProduction') return c.next ? 'Mark production' : 'Clear flag';
  return c.next ? 'Enable' : 'Disable';
}

function settingsConfirmTitle(c: SettingsChange): string {
  return c.field === 'commandsEnabled'
    ? `${c.next ? 'Enable' : 'Disable'} commands on ${c.clusterName}?`
    : `Clear the production flag on ${c.clusterName}?`;
}

function settingsConfirmBody(c: SettingsChange): string {
  if (c.field === 'isProduction') {
    return `A maintainer will be able to approve MUTATING commands on ${c.clusterName}; today that needs root. Only clear this on a cluster that genuinely is not production.`;
  }
  return c.next
    ? `Approved commands become dispatchable to ${c.clusterName}. The cluster's agents must also have AGENT_COMMANDS_ENABLED=true in their ConfigMap — this switch alone does nothing.`
    : `New commands can no longer be approved for ${c.clusterName}. Anything already queued or running is unaffected.`;
}

/** Why an action button is disabled — never leave a dead control unexplained. */
function opTitle(commandsEnabled: boolean, supported: boolean, op: string): string {
  if (!commandsEnabled) return 'Commands are disabled for this cluster';
  if (!supported) return `This agent build does not advertise ${op}`;
  return op;
}

function ClusterTokensSection({
  tokens,
  onChange,
}: {
  tokens: ClusterToken[];
  onChange: () => void;
}) {
  const [newCluster, setNewCluster] = useState('');
  const [issued, setIssued] = useState<{ clusterName: string; token: string } | null>(null);

  const [mint, mintState] = useMutation<{ mintClusterAgentToken: { clusterName: string; token: string } }>(MINT, {
    onCompleted: (d) => {
      setIssued(d.mintClusterAgentToken);
      setNewCluster('');
      onChange();
    },
  });
  const [rotate, rotateState] = useMutation<{ rotateClusterAgentToken: { clusterName: string; token: string } }>(
    ROTATE,
    { onCompleted: (d) => { setIssued(d.rotateClusterAgentToken); onChange(); } },
  );
  const [setActive] = useMutation(SET_ACTIVE, { onCompleted: onChange });

  const busy = mintState.loading || rotateState.loading;

  return (
    <section style={{ marginTop: '1.5rem' }}>
      <h3>Agent cluster tokens</h3>
      <p className="muted" style={{ marginBottom: '0.5rem' }}>
        Manage agent credentials for each cluster. New and rotated tokens are shown only once.
      </p>

      {issued && (
        <p className="detail-desc" style={{ background: 'var(--panel)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
          {/* The TOKEN stays in <code> — a secret being copied verbatim is one of
              the uses the mono policy explicitly keeps. The cluster name is not. */}
          Token for {issued.clusterName} — copy now, it won&rsquo;t be shown again:
          <br />
          <code style={{ userSelect: 'all', wordBreak: 'break-all' }}>{issued.token}</code>
          <br />
          <button type="button" className="btn btn-ghost" onClick={() => setIssued(null)}>
            Dismiss
          </button>
        </p>
      )}

      <div className="filter-row" style={{ marginBottom: '0.75rem' }}>
        <input
          className="class-select"
          placeholder="cluster name (e.g. k8s-optisigns-qa)"
          value={newCluster}
          onChange={(e) => setNewCluster(e.target.value)}
          aria-label="cluster name"
          style={{ minWidth: 280 }}
        />
        <button
          type="button"
          className="btn btn-primary"
          disabled={!newCluster.trim() || busy}
          onClick={() => mint({ variables: { clusterName: newCluster.trim() } })}
        >
          {mintState.loading ? 'Minting…' : 'Mint token'}
        </button>
      </div>
      {(mintState.error || rotateState.error) && (
        <>
          <p className="error">Couldn&apos;t issue the token.</p>
          <p className="muted">
            No token was created or rotated, so the cluster&apos;s agents keep working with the one
            they have. Check the cluster name and retry.
          </p>
        </>
      )}

      {!tokens.length ? (
        <p className="muted">No cluster tokens yet.</p>
      ) : (
        <div className="operations-table-scroll" tabIndex={0} role="region" aria-label="Agent records"><table className="mtx">
          <thead>
            <tr>
              <th>Cluster</th>
              <th>Active</th>
              <th>Last used</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.clusterName}>
                <td>{t.clusterName}</td>
                <td>
                  <span className={`badge ${t.isActive ? 'ok' : 'quiet'}`}>{t.isActive ? 'active' : 'disabled'}</span>
                </td>
                <td className="muted">{t.lastUsedAt ? formatDateTime(t.lastUsedAt) : 'Never'}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={busy}
                    onClick={() => rotate({ variables: { clusterName: t.clusterName } })}
                  >
                    Rotate
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setActive({ variables: { clusterName: t.clusterName, active: !t.isActive } })}
                  >
                    {t.isActive ? 'Disable' : 'Enable'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </section>
  );
}
